import { useTranslation } from 'react-i18next';
import { formatDateForDisplay } from '../../../utils';
import { BookingApprovalDetails } from '../../../types/bookingCreation';
import { approvalLabelKey, toApprovalState } from '../../../utils/bookingApproval';

/**
 * M8 approval (#1809): the sub-status beside the status badge of a room
 * booking's detail page — "noch nicht freigegeben", "freigegeben",
 * "abgelehnt". Same muted label as on the bookings list. Nothing when the
 * booking has no approval sub-status (booked, paid, an ordinary cancel).
 */
export const BookingApprovalLabel = ({ approval }: { approval?: BookingApprovalDetails }) => {
    const { t } = useTranslation();
    const state = toApprovalState(approval?.state);
    if (!state) return null;
    return (
        <span className={'ml-2 self-center text-xs text-foreground/50'}>
            {t(approvalLabelKey(state))}
        </span>
    );
};

type Props = {
    approval?: BookingApprovalDetails;
    /** #3219: the booking location's zone; the station default when omitted. */
    timeZone?: string;
};

/**
 * Who approved or declined the reservation, and when, in the facts column of
 * the detail page — beside "Vertrag geschlossen am" (`ContractConcludedInfo`).
 *
 * An approval without a name was recorded by a master or set from earlier
 * evidence (the one-off backfill), so it shows the date alone rather than
 * inventing an approver. The decline reason is internal and shown to staff
 * only.
 *
 * Shared by the venue and apartment detail pages, which are near-copies.
 */
const BookingApprovalInfo = ({ approval, timeZone }: Props) => {
    const { t } = useTranslation();
    const state = toApprovalState(approval?.state);
    if (!approval || !state || state === 'awaiting') return null;

    if (state === 'approved' && approval.approvedAt) {
        return (
            <p className={'text-sm text-foreground/60 mt-1'}>
                {t('booking_details.approval_info.approved_at')}:{' '}
                <span className={'font-bold'}>{formatDateForDisplay(approval.approvedAt, timeZone)}</span>
                {approval.approvedByName && (
                    <> {t('booking_details.approval_info.by', { name: approval.approvedByName })}</>
                )}
            </p>
        );
    }

    if (state === 'declined' && approval.declinedAt) {
        return (
            <>
                <p className={'text-sm text-foreground/60 mt-1'}>
                    {t('booking_details.approval_info.declined_at')}:{' '}
                    <span className={'font-bold'}>{formatDateForDisplay(approval.declinedAt, timeZone)}</span>
                    {approval.declinedBy && (
                        <> {t('booking_details.approval_info.by', { name: approval.declinedBy })}</>
                    )}
                </p>
                {approval.declineReason && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('booking_details.approval_info.decline_reason')}:{' '}
                        <span className={'whitespace-pre-line'}>{approval.declineReason}</span>
                    </p>
                )}
            </>
        );
    }

    return null;
};

export default BookingApprovalInfo;
